// Only explicitly selected read requests may recover from transient transport failures.
// One shared signal bounds all attempts and cancels retries when the workspace changes.
export async function fetchWithReadRetry(url: string, init: RequestInit, retryRead: boolean, retryEventConflict = false): Promise<Response> {
  // Event reads fence shared authority records on the server. Avoid making one tab's
  // panels contend with each other; this schedules transport only, never authorization.
  const release = retryEventConflict && (init.method || "GET") === "GET" ? await acquireEventRead(init.signal) : undefined;
  try { return await fetchAttempts(url, init, retryRead, retryEventConflict); }
  finally { release?.(); }
}

let eventReadActive = false;
const eventReadWaiters: Array<() => void> = [];
function acquireEventRead(signal?: AbortSignal | null): Promise<() => void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const grant = () => {
      signal?.removeEventListener("abort", cancel);
      resolve(() => {
        const next = eventReadWaiters.shift();
        if (next) next(); else eventReadActive = false;
      });
    };
    const cancel = () => {
      const index = eventReadWaiters.indexOf(grant);
      if (index >= 0) eventReadWaiters.splice(index, 1);
      signal?.removeEventListener("abort", cancel);
      reject(signal?.reason);
    };
    if (!eventReadActive) { eventReadActive = true; grant(); }
    else { eventReadWaiters.push(grant); signal?.addEventListener("abort", cancel, { once: true }); }
  });
}

async function fetchAttempts(url: string, init: RequestInit, retryRead: boolean, retryEventConflict: boolean): Promise<Response> {
  retryRead = retryRead && (init.method || "GET") === "GET";
  retryEventConflict = retryEventConflict && (init.method || "GET") === "GET";
  const delays = [500, 1500];
  for (let attempt = 0; ; attempt++) {
    init.signal?.throwIfAborted();
    try {
      const response = await fetch(url, init);
      if (attempt >= delays.length) return response;
      let eventConflict = false;
      if (retryEventConflict && [400, 409].includes(response.status)) {
        const payload = await response.clone().json().catch(() => null);
        eventConflict = payload?.success === false && payload?.errorCode === "EVENT_UNIT_CONCURRENT_CHANGE";
      }
      if (!eventConflict && !(retryRead && [502, 503, 504].includes(response.status))) return response;
      await response.body?.cancel();
    } catch (error) {
      if (!retryRead || attempt >= delays.length || !(error instanceof TypeError)) throw error;
    }
    // Spread competing panels across the backoff window instead of retrying in lockstep.
    await waitForRetry(delays[attempt] + (retryEventConflict ? Math.floor(Math.random() * 500) : 0), init.signal);
  }
}

function waitForRetry(delay: number, signal?: AbortSignal | null): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const cancel = () => { clearTimeout(timer); signal?.removeEventListener("abort", cancel); reject(signal?.reason); };
    const timer = setTimeout(() => { signal?.removeEventListener("abort", cancel); resolve(); }, delay);
    signal?.addEventListener("abort", cancel, { once: true });
  });
}
